import { useCallback, useEffect, useRef, useState, type ReactNode, type RefObject } from 'react';
import { createPortal } from 'react-dom';
import { computeMenuPosition, type MenuPosition } from './menuPosition';

interface AnchoredMenuProps {
  /** The trigger element the menu hangs off. */
  anchorRef: RefObject<HTMLElement | null>;
  open: boolean;
  /** Fired on outside click, Escape, or an item choosing to dismiss. */
  onClose: () => void;
  id?: string;
  /** Extra classes on the menu surface (e.g. a wider `min-w-`). */
  className?: string;
  children: ReactNode;
}

/**
 * A dropdown menu rendered in a portal on `document.body` and positioned with
 * `position: fixed` against its trigger.
 *
 * The portal is the point: the list pages wrap their tables in a
 * `rounded-lg overflow-hidden` container, and `overflow: hidden` clips
 * absolutely-positioned descendants. A menu on the last row was therefore cut
 * off at the table's bottom edge and unreachable — it was clipped, not merely
 * off-screen, so scrolling didn't help. Rendering outside that container fixes
 * it without giving up the rounded corners.
 *
 * Placement (below, flipping above near the viewport bottom) lives in the pure,
 * unit-tested `computeMenuPosition`.
 */
export default function AnchoredMenu({
  anchorRef,
  open,
  onClose,
  id,
  className = '',
  children,
}: AnchoredMenuProps) {
  const menuRef = useRef<HTMLDivElement | null>(null);
  /** Border-box height captured while the menu is still unconstrained. */
  const naturalHeightRef = useRef(0);
  const [position, setPosition] = useState<MenuPosition | null>(null);

  const reposition = useCallback(() => {
    const anchor = anchorRef.current;
    const menu = menuRef.current;
    if (!anchor || !menu || naturalHeightRef.current === 0) return;
    setPosition(
      computeMenuPosition(
        anchor.getBoundingClientRect(),
        { width: menu.offsetWidth, height: naturalHeightRef.current },
        { width: window.innerWidth, height: window.innerHeight },
      ),
    );
  }, [anchorRef]);

  // Measure via a callback ref rather than an effect: the menu is positioned
  // before it is ever painted (no flash at 0,0), and there is no state-set
  // inside an effect for the react-hooks lint to flag.
  //
  // The height is taken here, once, while no maxHeight has been applied yet.
  // Re-measuring on every reposition would feed the clamped height back in and
  // shrink the menu a little each time; and it must be the BORDER-box height
  // (getBoundingClientRect) rather than scrollHeight, because `max-height`
  // resolves against the border box under Tailwind's global `border-box` sizing
  // — mixing the two clamps the menu ~2px short and grows a stray scrollbar.
  const attachMenu = useCallback(
    (node: HTMLDivElement | null) => {
      menuRef.current = node;
      if (!node) {
        naturalHeightRef.current = 0;
        return;
      }
      naturalHeightRef.current = node.getBoundingClientRect().height;
      reposition();
    },
    [reposition],
  );

  useEffect(() => {
    if (!open) return;
    const handlePointerDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (menuRef.current?.contains(target)) return;
      // Ignore the trigger itself, or its own onClick would immediately
      // re-open what this handler just closed.
      if (anchorRef.current?.contains(target)) return;
      onClose();
    };
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKey);
    window.addEventListener('resize', reposition);
    // Capture phase so a scroll inside any container — not just the window —
    // keeps the menu pinned to its row.
    window.addEventListener('scroll', reposition, true);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKey);
      window.removeEventListener('resize', reposition);
      window.removeEventListener('scroll', reposition, true);
    };
  }, [open, onClose, reposition, anchorRef]);

  if (!open) return null;

  return createPortal(
    <div
      id={id}
      role="menu"
      ref={attachMenu}
      style={{
        position: 'fixed',
        top: position?.top ?? 0,
        left: position?.left ?? 0,
        maxHeight: position?.maxHeight,
        overflowY: 'auto',
        // Hidden for the single measure-then-place frame.
        visibility: position ? 'visible' : 'hidden',
      }}
      className={`z-50 min-w-[220px] bg-surface border border-outline-variant rounded-lg shadow-pop py-1 text-sm ${className}`}
    >
      {children}
    </div>,
    document.body,
  );
}
